import { useCallback, useMemo, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { BuildingLibraryIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useAuth } from '@/context/AuthContext';
import { useOrgLockState } from '@/hooks/useOrgLockState';
import PausedChip from '@/components/lock/PausedChip';
import GroupRailListItem, { type SidebarGroup, type RailBadge } from './GroupRailListItem';

interface OrgNode {
  orgId: string;
  orgName: string;
  groups: SidebarGroup[];
}

interface Props {
  selectedGroupId: string | null;
  onSelectGroup: (group: SidebarGroup) => void;
  /** localStorage key for the collapsed-org set (per feature). */
  collapseKey: string;
  /** Optional per-group count bubbles (e.g. open polls). */
  badges?: Record<string, RailBadge>;
}

/**
 * Generic cross-org group sidebar (PrayerSidebar pattern minus unread badges
 * and pinned combined views): search box, then Organization (collapsible) →
 * community groups. Built from memberships, so every group appears even
 * before it has any content.
 */
export default function GroupRailSidebar({
  selectedGroupId,
  onSelectGroup,
  collapseKey,
  badges,
}: Props) {
  const { organizations } = useAuth();
  const { isOrgLocked } = useOrgLockState();
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(collapseKey);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleCollapsed = useCallback(
    (key: string) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        try {
          localStorage.setItem(collapseKey, JSON.stringify(Array.from(next)));
        } catch {
          // Ignore storage errors
        }
        return next;
      });
    },
    [collapseKey]
  );

  const tree = useMemo((): OrgNode[] => {
    const q = search.trim().toLowerCase();
    const orgs: OrgNode[] = [];
    for (const membership of organizations) {
      const orgName = membership.organization.name;
      const groups: SidebarGroup[] = (membership.groups ?? [])
        .filter(
          (g) => !q || g.name.toLowerCase().includes(q) || orgName.toLowerCase().includes(q)
        )
        .map((g) => ({
          id: g.id,
          name: g.name,
          orgId: membership.organization_id,
          orgName,
          imageUrl: g.group_image_url,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      if (groups.length === 0) continue;
      orgs.push({ orgId: membership.organization_id, orgName, groups });
    }
    orgs.sort((a, b) => a.orgName.localeCompare(b.orgName));
    return orgs;
  }, [organizations, search]);

  const searching = search.trim().length > 0;
  const hasAnyGroups = organizations.some((o) => (o.groups?.length ?? 0) > 0);

  return (
    <div className="flex h-full w-80 shrink-0 flex-col border-r border-flock-gray-200 bg-white">
      <div className="border-b border-flock-gray-200 p-3">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-flock-navy-300" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search groups"
            className="w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 py-2 pl-9 pr-3 text-sm text-flock-navy-900 outline-none placeholder:text-flock-navy-300 focus:border-flock-navy-400"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {!hasAnyGroups && (
          <div className="p-4 text-center text-sm text-flock-navy-400">
            You're not in any groups yet.
          </div>
        )}

        {hasAnyGroups && searching && tree.length === 0 && (
          <div className="p-4 text-center text-sm text-flock-navy-400">
            No groups match your search.
          </div>
        )}

        {tree.map((org) => {
          const orgKey = `org:${org.orgId}`;
          // While searching, force everything open so matches are visible
          const orgCollapsed = !searching && collapsed.has(orgKey);
          // Aggregate group badges for the collapsed-org header pill
          const orgBadge = org.groups.reduce(
            (acc, g) => {
              const b = badges?.[g.id];
              if (!b) return acc;
              return { count: acc.count + b.count, urgent: acc.urgent || b.urgent };
            },
            { count: 0, urgent: false }
          );
          const orgLocked = isOrgLocked(org.orgId);
          return (
            <div key={org.orgId} className="mb-1">
              <button
                onClick={() => toggleCollapsed(orgKey)}
                className="flex w-full items-center gap-1.5 rounded-lg px-2 py-2 text-left hover:bg-flock-navy-50"
              >
                {orgCollapsed ? (
                  <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-flock-navy-400" />
                ) : (
                  <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-flock-navy-400" />
                )}
                <BuildingLibraryIcon className="h-4 w-4 shrink-0 text-flock-navy-500" />
                <span className="min-w-0 flex-1 truncate text-xs font-bold uppercase tracking-wide text-flock-navy-700">
                  {org.orgName}
                </span>
                {orgLocked && <PausedChip />}
                {orgCollapsed && orgBadge.count > 0 && (
                  <span
                    className={clsx(
                      'flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold',
                      orgBadge.urgent
                        ? 'bg-[#E63946] text-white'
                        : 'bg-flock-gray-300 text-flock-navy-700'
                    )}
                  >
                    {orgBadge.count > 99 ? '99+' : orgBadge.count}
                  </span>
                )}
              </button>

              {!orgCollapsed && (
                <div className="space-y-0.5">
                  {org.groups.map((group) => (
                    <GroupRailListItem
                      key={group.id}
                      group={group}
                      selected={selectedGroupId === group.id}
                      onSelect={onSelectGroup}
                      badge={badges?.[group.id]}
                      locked={orgLocked}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
